import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';

const VideoCarouselCard = ({ item, activeOption }) => {
  const videoRef = useRef(null);
  const videoSrcRef = useRef(null);
  const isPlayingRef = useRef(false);
  const { ref: inViewRef, inView } = useInView({ threshold: 0.5 });
  const [playState, updatePlayState] = useState('paused');
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  
  const combinedRef = useCallback(
    (node) => {
      videoRef.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );
  
  const brandSlug = item.name.toLowerCase().trim().replace(/ /g, '-');
  const location = useLocation();

  // Safe video control functions with error handling
  const playVideo = useCallback(async () => {
    if (!videoRef.current || isPlayingRef.current) {
      return;
    }
    
    try {
      isPlayingRef.current = true;
      updatePlayState('playing');
      await videoRef.current.play();
    } catch (error) {
      console.warn('Video play failed:', error);
      isPlayingRef.current = false;
      updatePlayState('paused');
    }
  }, []);

  const pauseVideo = useCallback(() => {
    if (!videoRef.current || !isPlayingRef.current) return;
    
    try {
      isPlayingRef.current = false;
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      updatePlayState('paused');
    } catch (error) {
      console.warn('Video pause failed:', error);
    }
  }, []);

  const handleMouseEnter = () => {
    playVideo();
  };

  const handleMouseLeave = () => {
    pauseVideo();
  };

  const handleVideoLoaded = () => {
    setIsVideoLoaded(true);
  };

  const handleVideoLoadStart = () => {
    setIsVideoLoaded(false);
  };


  // Initialize video on mount
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      updatePlayState('paused');
      isPlayingRef.current = false;
    }
  }, []); // Only run once on mount

  // Track video source changes separately
  useEffect(() => {
    const newSrc = activeOption === 'Video' ? item['16:9']?.[0]?.url : item['300x250']?.[0]?.url;
    if (videoSrcRef.current !== newSrc) {
      videoSrcRef.current = newSrc;
      setIsVideoLoaded(false);
      isPlayingRef.current = false;
      updatePlayState('paused'); // Only reset playState when source actually changes
    }
  }, [item, activeOption]);

  // Handle mobile autoplay when in view
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile && inView && videoRef.current) {
      playVideo();
    } else if (!inView) {
      pauseVideo();
    }
  }, [inView]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    };
  }, []);

  if (activeOption === 'Video') {
    if (!item || !item['16:9']) return null;
    const videoUrlVideo = item['16:9'][0].url;
    return (
      <Link to={`${location.pathname}video/${brandSlug}`} className={`videoCard ${playState}`}>
        <div key={item.index}>
          <video
            key={`video-${item.index}-${videoUrlVideo}`}
            ref={combinedRef}
            type="video/mp4"
            width="365px"
            height="600px"
            muted
            controls={false}
            playsInline
            autoPlay={false}
            src={videoUrlVideo}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onLoadStart={handleVideoLoadStart}
            onLoadedData={handleVideoLoaded}
            className={isVideoLoaded ? 'video-fade-in' : ''}
          />
        </div>
      </Link>
    );
  }
  if (activeOption === 'Display') {
    if (!item || !item['300x250']) return null;
    const videoUrl300x250 = item['300x250'][0].url;
    return (
      <Link to={`${location.pathname}display/${brandSlug}`} className={`displayCard ${playState}`}>
        <div key={item.index}>
          <video
            key={`display-${item.index}-${videoUrl300x250}`}
            ref={combinedRef}
            width="300"
            type="video/mp4"
            height="250"
            muted
            playsInline
            controls={false}
            src={videoUrl300x250}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onLoadStart={handleVideoLoadStart}
            onLoadedData={handleVideoLoaded}
            className={isVideoLoaded ? 'video-fade-in' : ''}
          />
        </div>
      </Link>
    );
  }
};

export default memo(VideoCarouselCard);
